import { Box } from "@ui/box/Box";
import { Typo, TypoCaption } from "@ui/text/Typo";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";
import { FC, PropsWithChildren, ReactNode } from "react";
import { createGlobalStyle } from "styled-components";
import { DS } from "../ds/Kit";

type AuthCardSize = "compact" | "wide";
export type AuthIconKind = "mail" | "success" | "error" | "warning" | "shield" | "loading";

type AuthCardProps = PropsWithChildren & {
	size?: AuthCardSize;
	eyebrow?: string;
	title?: ReactNode;
	subtitle?: ReactNode;
	/** Optional state icon above the title (confirmation, error and loading screens). */
	icon?: AuthIconKind;
};

// Width of the form column. Signup needs room for two fields per row.
const cardWidths: Record<AuthCardSize, string> = {
	compact: "420px",
	wide: "600px",
};

const SPLIT = "@media (min-width: 900px)";
const STACKED = "@media (max-width: 899px)";

// The split layout covers the whole screen and carries its own legal links, so the
// start screen's footer links (hidden behind it) must not stay reachable by keyboard.
const HideStartScreenLegal = createGlobalStyle`
	[data-start-legal] { display: none !important; }
`;

const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" } as const;

const BRAND_POINTS = [
	{ text: "Issue virtual cards in seconds", icon: <><rect x="2" y="5" width="20" height="14" rx="3" /><path d="M2 10h20" /></> },
	{ text: "Control spend with limits and freezes", icon: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></> },
	{ text: "Follow every transaction clearly", icon: <><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></> },
];

const BrandPanel = () => (
	<Box
		sx={{
			position: "relative",
			overflow: "hidden",
			display: "flex",
			flexDirection: "column",
			justifyContent: "space-between",
			boxSizing: "border-box",
			background: DS.heroGradient,
			color: "#FFFFFF",
			[SPLIT]: { position: "sticky", top: 0, alignSelf: "start", height: "100dvh", padding: "44px clamp(32px, 4vw, 64px)" },
			[STACKED]: { padding: "14px 20px" },
		}}>
		<Box aria-hidden="true" data-brand-extra="true" sx={{ position: "absolute", right: "-70px", bottom: "120px", width: "340px", height: "210px", borderRadius: "24px", transform: "rotate(-14deg)", background: "linear-gradient(135deg, rgba(255,255,255,.26), rgba(255,255,255,.05))", border: "1px solid rgba(255,255,255,.28)", [STACKED]: { display: "none" } }} />
		<Box aria-hidden="true" data-brand-extra="true" sx={{ position: "absolute", right: "30px", bottom: "-50px", width: "300px", height: "190px", borderRadius: "22px", transform: "rotate(8deg)", background: "linear-gradient(135deg, rgba(255,255,255,.16), rgba(255,255,255,.03))", border: "1px solid rgba(255,255,255,.2)", [STACKED]: { display: "none" } }} />
		<Box aria-hidden="true" data-brand-extra="true" sx={{ position: "absolute", left: "-120px", top: "-120px", width: "320px", height: "320px", borderRadius: "50%", background: "rgba(255,255,255,.07)", [STACKED]: { display: "none" } }} />

		<Box aria-label="Cardpartner" sx={{ position: "relative", display: "flex", alignItems: "center", gap: "12px" }}>
			<Box aria-hidden="true" sx={{ width: "40px", height: "40px", flex: "0 0 40px", borderRadius: "12px", display: "grid", placeItems: "center", background: "rgba(255,255,255,.18)", border: "1px solid rgba(255,255,255,.3)", color: "#FFFFFF", fontSize: "13px", fontWeight: 800, letterSpacing: "-.02em" }}>
				CP
			</Box>
			<Box sx={{ fontSize: "18px", fontWeight: 700, letterSpacing: "-.02em", lineHeight: 1.15 }}>Cardpartner</Box>
		</Box>

		<Box data-brand-extra="true" sx={{ position: "relative", maxWidth: "440px", [STACKED]: { display: "none" } }}>
			<Typo sx={{ color: "#FFFFFF", fontSize: "clamp(30px, 3.2vw, 40px)", lineHeight: 1.12, fontWeight: 700, letterSpacing: "-.03em" }}>
				Business cards, under your control.
			</Typo>
			<Box sx={{ marginTop: "32px", display: "grid", gap: "16px" }}>
				{BRAND_POINTS.map(point => (
					<Box key={point.text} sx={{ display: "flex", alignItems: "center", gap: "14px", fontSize: "15px", lineHeight: 1.4, color: "rgba(255,255,255,.92)" }}>
						<Box aria-hidden="true" sx={{ width: "36px", height: "36px", flex: "0 0 36px", borderRadius: "11px", display: "grid", placeItems: "center", background: "rgba(255,255,255,.16)", border: "1px solid rgba(255,255,255,.24)" }}>
							<svg width="18" height="18" viewBox="0 0 24 24" {...stroke}>{point.icon}</svg>
						</Box>
						{point.text}
					</Box>
				))}
			</Box>
		</Box>

		<Box data-brand-extra="true" sx={{ position: "relative", height: "1px", [STACKED]: { display: "none" } }} />
	</Box>
);

const LegalLinks = () => (
	<Box
		role="navigation"
		aria-label="Legal"
		sx={{
			display: "flex",
			flexWrap: "wrap",
			justifyContent: "center",
			alignItems: "center",
			gap: "0 18px",
			marginTop: "32px",
			fontSize: "12px",
			lineHeight: 1.5,
			"& a, & button": { display: "inline-flex", alignItems: "center", minHeight: "44px", padding: 0, border: 0, background: "transparent", color: DS.muted, font: "inherit", textDecoration: "underline", textUnderlineOffset: "3px", cursor: "pointer" },
			"& a:hover, & button:hover": { color: DS.ink },
			"& a:focus-visible, & button:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "3px", borderRadius: "4px" },
		} as RecursiveCSS}>
		<a href="/terms.html">Terms and Conditions</a>
		<a href="/cookies.html">Cookies Policy</a>
		<a href="/privacy.html">Privacy Policy</a>
		<button type="button" data-cookie-settings>
			Cookie preferences
		</button>
	</Box>
);

const ICON_STYLES: Record<AuthIconKind, { background: string; color: string; glyph: ReactNode }> = {
	mail: { background: DS.accentSoft, color: DS.accent, glyph: <><rect x="3" y="5" width="18" height="14" rx="3" /><path d="m3 8 9 6 9-6" /></> },
	success: { background: "#ECFDF5", color: "#047857", glyph: <path d="m5 12.5 4.5 4.5L19 7.5" /> },
	error: { background: "#FEF2F2", color: "#B91C1C", glyph: <><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.5M12 16.5h.01" /></> },
	warning: { background: "#FFFBEB", color: "#B45309", glyph: <><path d="M12 3 2.5 20h19L12 3z" /><path d="M12 10v4.5M12 17.5h.01" /></> },
	shield: { background: DS.accentSoft, color: DS.accent, glyph: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></> },
	loading: { background: DS.accentSoft, color: DS.accent, glyph: <><path d="M21 12a9 9 0 1 1-6.2-8.55" /></> },
};

/** Round state icon shown above the title on confirmation, error and loading screens. */
export const AuthIcon = ({ kind }: { kind: AuthIconKind }) => {
	const style = ICON_STYLES[kind];
	return (
		<Box aria-hidden="true" sx={{ width: "56px", height: "56px", marginBottom: "18px", borderRadius: "50%", display: "grid", placeItems: "center", background: style.background, color: style.color }}>
			<svg width="26" height="26" viewBox="0 0 24 24" {...stroke}>{style.glyph}</svg>
		</Box>
	);
};

export const AuthCard: FC<AuthCardProps> = ({ size = "compact", eyebrow, title, subtitle, icon, children }) => (
	<Box
		data-auth-surface="true"
		sx={{
			position: "fixed",
			inset: 0,
			zIndex: 1,
			width: "100%",
			minWidth: 0,
			boxSizing: "border-box",
			backgroundColor: DS.surface,
			color: DS.ink,
			overflowX: "hidden",
			overflowY: "auto",
			overscrollBehavior: "contain",
			display: "flex",
			flexDirection: "column",
			[SPLIT]: { display: "grid", gridTemplateColumns: "minmax(360px, 5fr) minmax(0, 6fr)" },
			"& a:focus-visible": {
				outline: `3px solid rgba(30, 58, 95, .25)`,
				outlineOffset: "2px",
			} as RecursiveCSS,
			"& input:focus": {
				outline: "none",
			} as RecursiveCSS,
			"& div:has(> input:focus)": {
				borderColor: DS.accent,
				boxShadow: "0 0 0 3px rgba(30, 58, 95, .14)",
			} as RecursiveCSS,
		}}>
		<HideStartScreenLegal />
		<BrandPanel />
		<Box sx={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", boxSizing: "border-box", padding: "40px clamp(20px, 4vw, 48px) 16px", [STACKED]: { padding: "28px 20px 16px" } }}>
			<Box
				data-auth-size={size}
				sx={{
					width: "100%",
					maxWidth: cardWidths[size],
					minWidth: 0,
					margin: "auto",
					boxSizing: "border-box",
				}}>
				{eyebrow || title || subtitle || icon ? <AuthHeader eyebrow={eyebrow} title={title} subtitle={subtitle} icon={icon} /> : null}
				{children}
			</Box>
			<LegalLinks />
		</Box>
	</Box>
);

export const AuthHeader = ({ eyebrow, title, subtitle, icon }: Omit<AuthCardProps, "children" | "size">) => (
	<Box sx={{ marginBottom: "28px" }}>
		{icon ? <AuthIcon kind={icon} /> : null}
		{eyebrow ? (
			<Box sx={{ marginBottom: "8px", color: DS.accent, fontSize: "13px", fontWeight: 600, lineHeight: 1.3 }}>
				{eyebrow}
			</Box>
		) : null}
		{title ? (
			<Typo sx={{ color: DS.ink, fontSize: "clamp(24px, 3vw, 30px)", lineHeight: 1.18, fontWeight: 700, letterSpacing: "-.03em" }}>
				{title}
			</Typo>
		) : null}
		{subtitle ? (
			<Typo sx={{ marginTop: "8px", color: DS.muted, fontSize: "15px", lineHeight: 1.55 }}>
				{subtitle}
			</Typo>
		) : null}
	</Box>
);

export const AuthDivider = ({ label }: { label?: string }) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: "12px", margin: "24px 0" }}>
		<Box aria-hidden="true" sx={{ height: "1px", flex: 1, backgroundColor: DS.line }} />
		{label ? <TypoCaption sx={{ color: DS.muted, fontSize: "12px", lineHeight: 1.3 }}>{label}</TypoCaption> : null}
		<Box aria-hidden="true" sx={{ height: "1px", flex: 1, backgroundColor: DS.line }} />
	</Box>
);

type AuthStatusTone = "neutral" | "success" | "warning" | "error";

const statusColors: Record<AuthStatusTone, { background: string; border: string; accent: string }> = {
	neutral: { background: DS.accentSoft, border: "#DDE1FF", accent: DS.accent },
	success: { background: "#ECFDF5", border: "#A7F3D0", accent: "#047857" },
	warning: { background: "#FFFBEB", border: "#FDE68A", accent: "#B45309" },
	error: { background: "#FEF2F2", border: "#FECACA", accent: "#B91C1C" },
};

const statusGlyphs: Record<AuthStatusTone, ReactNode> = {
	neutral: <><path d="M12 11v5" /><path d="M12 7.5h.01" /></>,
	success: <path d="m6 12.5 4 4L18.5 8" />,
	warning: <><path d="M12 7.5v5" /><path d="M12 16.5h.01" /></>,
	error: <><path d="M12 7.5v5" /><path d="M12 16.5h.01" /></>,
};

export const AuthStatus = ({ tone = "neutral", title, children }: PropsWithChildren<{ tone?: AuthStatusTone; title?: ReactNode }>) => {
	const colors = statusColors[tone];

	return (
		<Box
			role={tone === "error" ? "alert" : "status"}
			sx={{
				padding: "14px 16px",
				display: "grid",
				gridTemplateColumns: "24px minmax(0, 1fr)",
				gap: "12px",
				alignItems: "start",
				border: `1px solid ${colors.border}`,
				borderRadius: "12px",
				backgroundColor: colors.background,
			}}>
			<Box aria-hidden="true" sx={{ width: "24px", height: "24px", borderRadius: "50%", display: "grid", placeItems: "center", backgroundColor: colors.accent, color: "#FFFFFF" }}>
				<svg width="14" height="14" viewBox="0 0 24 24" {...stroke} strokeWidth={2.6}>{statusGlyphs[tone]}</svg>
			</Box>
			<Box sx={{ minWidth: 0 }}>
				{title ? <Typo sx={{ color: DS.ink, fontSize: "14px", lineHeight: 1.4, fontWeight: 600 }}>{title}</Typo> : null}
				{children ? <Typo sx={{ marginTop: title ? "2px" : 0, color: DS.text, fontSize: "13px", lineHeight: 1.5, overflowWrap: "anywhere" }}>{children}</Typo> : null}
			</Box>
		</Box>
	);
};

export const AUTH_PRIMARY_BUTTON_SX: RecursiveCSS = {
	width: "100%",
	minHeight: "48px",
	justifyContent: "center",
	borderRadius: "12px",
	fontWeight: 600,
	background: DS.gradient,
	color: "#FFFFFF",
	border: "1px solid transparent",
	boxShadow: "0 6px 16px rgba(30, 58, 95, .3)",
	"&:hover:not(:disabled)": { filter: "brightness(1.06)" } as RecursiveCSS,
};

export const AUTH_SECONDARY_BUTTON_SX: RecursiveCSS = {
	width: "100%",
	minHeight: "46px",
	justifyContent: "center",
	borderRadius: "12px",
	fontWeight: 600,
	backgroundColor: DS.surface,
	color: DS.ink,
	border: `1px solid ${DS.line}`,
	boxShadow: "0 1px 2px rgba(15, 23, 42, .04)",
	"&:hover:not(:disabled)": { backgroundColor: DS.soft } as RecursiveCSS,
};

export const AUTH_FORM_ACTIONS_SX: RecursiveCSS = {
	position: "static",
	marginTop: "20px",
	paddingTop: 0,
	paddingBottom: 0,
	borderTop: "0 solid transparent",
	backgroundColor: "transparent",
	boxShadow: "none",
	backdropFilter: "none",
};

export const AUTH_TWO_COLUMN_SX: RecursiveCSS = {
	display: "grid",
	gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
	// Helper text in one field must not stretch the neighboring input control.
	alignItems: "start",
	gap: "16px",
	"@media (max-width: 620px)": {
		gridTemplateColumns: "minmax(0, 1fr)",
	} as RecursiveCSS,
};
